<template>
  <div class="chart-widget">
    <!-- Device Status Donut Chart -->
    <div v-if="widget.templateId === 'metric_device_status'" class="device-status-chart">
      <div class="chart-controls mb-2">
        <v-select
          v-model="selectedLocation"
          :items="availableLocations"
          item-text="name"
          item-value="id"
          dense
          outlined
          filled
          hide-details
          label="Filter by Location"
          style="max-width: 250px;"
          clearable
          placeholder="All Locations"
          no-data-text="No locations found"
        >
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title>{{ item.name }}</v-list-item-title>
              <v-list-item-subtitle v-if="item.deviceCount !== undefined">
                {{ item.deviceCount }} device{{ item.deviceCount !== 1 ? 's' : '' }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-select>
      </div>
      <div class="chart-container d-flex justify-center">
        <canvas :id="chartId"></canvas>
      </div>
    </div>
    
    <!-- Alerts by Location Bar Chart -->
    <div v-else-if="widget.templateId === 'metric_alerts_by_location'" class="alerts-location-chart">
      <div class="chart-container">
        <canvas :id="chartId" height="200"></canvas>
      </div>
    </div>
    
    <!-- Alerts Over Time Line Chart -->
    <div v-else-if="widget.templateId === 'metric_alerts_over_time'" class="alerts-timeline-chart">
      <div class="chart-controls mb-2">
        <div class="d-flex align-center" style="gap: 8px;">
          <br>
          <v-select
            v-model="timeRange"
            :items="timeRangeOptions"
            dense
            outlined
            filled
            label="Time Range"
            hide-details
            style="min-width: 15%; max-width: 25%;"
          ></v-select>
          <v-select
            v-model="selectedSensors"
            :items="availableSensors.filter(sensor => sensor.id !== null)"
            item-text="name"
            no-data-text="No sensors found"
            item-value="id"
            single-line
            placeholder="All Sensors"
            multiple
            chips
            dense
            filled
            outlined
            hide-details
            label="Filter by Sensors"
            style="width: 100%; min-width: 75%;"
            clearable
            class="chart-select"
            clear-icon="mdi-close"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip
                v-if="index < 2"
                small
                close
                @click:close="removeSensor(item.id)"
              >
                {{ item.name }}
              </v-chip>
              <span
                v-if="index === 2"
                class="grey--text text-caption"
              >
                (+{{ selectedSensors.length - 2 }} others)
              </span>
            </template>
          </v-select>
        </div>
      </div>
      <div class="chart-container">
        <canvas :id="chartId" height="200"></canvas>
      </div>
    </div>
    
    <!-- Battery Levels Bar Chart -->
    <div v-else-if="widget.templateId === 'metric_battery_levels'" class="battery-chart">
      <div class="chart-controls mb-2">
        <v-text-field
          v-model.number="batteryDeviceLimit"
          type="number"
          min="1"
          max="10"
          outlined
          dense
          filled
          hide-details
          label="Max Devices"
          style="max-width: 150px;"
        ></v-text-field>
      </div>
      <div class="chart-container">
        <canvas :id="chartId"></canvas>
      </div>
    </div>
    
    <!-- Work Orders Over Time Line Chart -->
    <div v-else-if="widget.templateId === 'metric_work_orders'" class="work-orders-chart">
      <div class="chart-controls mb-2">
        <v-select
          v-model="workOrderTimeRange"
          :items="timeRangeOptions"
          dense
          outlined
          filled
          hide-details
          label="Time Range"
          style="max-width: 100%;"
        ></v-select>
      </div>
      <div class="chart-container">
        <canvas :id="chartId"></canvas>
      </div>
    </div>
    
    <!-- Torque Deviation Scatter Chart -->
    <div v-else-if="widget.templateId === 'metric_torque_deviation'" class="torque-chart">
      <div class="chart-container">
        <canvas :id="chartId"></canvas>
      </div>
    </div>
    
    <!-- Gyro Data Line Chart -->
    <div v-else-if="widget.templateId === 'metric_gyro_accelerometer'" class="gyro-chart">
      <div class="chart-container">
        <canvas :id="chartId"></canvas>
      </div>
    </div>
    
    <!-- Accelerometer Data Line Chart -->
    <div v-else-if="widget.templateId === 'metric_accelerometer_data'" class="accel-chart">
      <div class="chart-container">
        <canvas :id="chartId"></canvas>
      </div>
    </div>
    
    <!-- Other Chart Types -->
    <div v-else class="generic-chart">
      <div class="chart-placeholder">
        <v-icon size="48" color="grey lighten-2">mdi-chart-bar</v-icon>
        <p class="text-body-2 grey--text mt-2">{{ widget.title }}</p>
        <p class="text-caption grey--text">Chart data will be loaded here</p>
      </div>
    </div>
  </div>
</template>

<script>
import { doughnutChart } from "@/components/Charts/Chart.js";
import Chart from 'chart.js/auto';
import { fetchObjects, getLocationsByUser, getWorkOrders } from '@/services/firebase-helpers';
import { getRawData } from '@/services/data';

export default {
  name: 'ChartWidget',
  props: {
    widget: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      chart: null,
      deviceStatusCounts: {
        Secure: 8,
        Warning: 4,
        Critical: 3
      },
      alertsByLocation: [],
      alertsOverTime: [],
      batteryLevels: [],
      workOrdersOverTime: [],
      torqueData: [],
      gyroData: [],
      accelData: [],
      timeRange: '90d',
      workOrderTimeRange: '90d',
      timeRangeOptions: [
        { text: '7 Days', value: '7d' },
        { text: '30 Days', value: '30d' },
        { text: '90 Days', value: '90d' }
      ],
      batteryDeviceLimit: 10,
      selectedSensors: [],
      availableSensors: [],
      selectedLocation: null,
      availableLocations: []
    }
  },
  computed: {
    chartId() {
      return `chart-${this.widget.id}`;
    },
    chartData() {
      if (this.widget.templateId === 'metric_device_status') {
        return {
          labels: ["Secure", "Warning", "Critical"],
          datasets: [{
            data: [
              this.deviceStatusCounts.Secure || 0,
              this.deviceStatusCounts.Warning || 0,
              this.deviceStatusCounts.Critical || 0
            ],
            backgroundColor: [
              "#4CAF50",  // Green for Secure
              "#FFC107",  // Yellow for Warning
              "#F44336",  // Red for Critical
            ],
            borderWidth: 0
          }]
        };
      } else if (this.widget.templateId === 'metric_alerts_by_location') {
        return {
          labels: this.alertsByLocation.map(item => item.locationName),
          datasets: [{
            label: 'Alerts',
            data: this.alertsByLocation.map(item => item.alertCount),
            backgroundColor: [
              '#2196F3', '#4CAF50', '#FF9800', '#9C27B0',
              '#F44336', '#00BCD4', '#8BC34A', '#FFC107'
            ],
            borderColor: [
              '#1976D2', '#388E3C', '#F57C00', '#7B1FA2',
              '#D32F2F', '#0097A7', '#689F38', '#FFA000'
            ],
            borderWidth: 1
          }]
        };
      } else if (this.widget.templateId === 'metric_alerts_over_time') {
        return {
          labels: this.alertsOverTime.map(item => item.date),
          datasets: [{
            label: 'Alerts',
            data: this.alertsOverTime.map(item => item.count),
            borderColor: '#F44336',
            backgroundColor: 'rgba(244, 67, 54, 0.1)',
            tension: 0.4,
            fill: true
          }]
        };
      } else if (this.widget.templateId === 'metric_battery_levels') {
        const limitedBatteryLevels = this.batteryLevels.slice(0, this.batteryDeviceLimit);
        return {
          labels: limitedBatteryLevels.map(item => item.deviceId),
          datasets: [{
            label: 'Battery Level (%)',
            data: limitedBatteryLevels.map(item => item.batteryLevel).sort((a, b) => b - a),
            backgroundColor: limitedBatteryLevels.map(item => 
              item.batteryLevel > 50 ? '#4CAF50' : 
              item.batteryLevel > 20 ? '#FF9800' : '#F44336'
            ),
            borderWidth: 1
          }]
        };
      } else if (this.widget.templateId === 'metric_work_orders') {
        return {
          labels: this.workOrdersOverTime.map(item => item.date),
          datasets: [{
            label: 'Work Orders Created',
            data: this.workOrdersOverTime.map(item => item.count),
            borderColor: '#2196F3',
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
            tension: 0.4,
            fill: true
          }]
        };
      } else if (this.widget.templateId === 'metric_torque_deviation') {
        return {
          datasets: [{
            label: 'Torque Deviation',
            data: this.torqueData,
            backgroundColor: 'rgba(156, 39, 176, 0.6)',
            borderColor: '#9C27B0',
            pointRadius: 4,
            pointHoverRadius: 6
          }]
        };
      } else if (this.widget.templateId === 'metric_gyro_accelerometer') {
        if (this.gyroData.length === 0) return null;
        
        return {
          datasets: [
            {
              label: 'Gyro X',
              data: this.gyroData.map(d => ({ x: new Date(d.timestamp), y: d.gyro_x })),
              borderColor: '#5e72e4',
              tension: 0.4,
              pointRadius: 0,
            },
            {
              label: 'Gyro Y', 
              data: this.gyroData.map(d => ({ x: new Date(d.timestamp), y: d.gyro_y })),
              borderColor: '#2dce89',
              tension: 0.4,
              pointRadius: 0,
            },
            {
              label: 'Gyro Z',
              data: this.gyroData.map(d => ({ x: new Date(d.timestamp), y: d.gyro_z })),
              borderColor: '#fb6340',
              tension: 0.4,
              pointRadius: 0,
            }
          ]
        };
      } else if (this.widget.templateId === 'metric_accelerometer_data') {
        if (this.accelData.length === 0) return null;
        
        return {
          datasets: [
            {
              label: 'Accel X',
              data: this.accelData.map(d => ({ x: new Date(d.timestamp), y: d.accel_x })),
              borderColor: '#5e72e4',
              tension: 0.4,
              pointRadius: 0,
            },
            {
              label: 'Accel Y', 
              data: this.accelData.map(d => ({ x: new Date(d.timestamp), y: d.accel_y })),
              borderColor: '#2dce89',
              tension: 0.4,
              pointRadius: 0,
            },
            {
              label: 'Accel Z',
              data: this.accelData.map(d => ({ x: new Date(d.timestamp), y: d.accel_z })),
              borderColor: '#fb6340',
              tension: 0.4,
              pointRadius: 0,
            }
          ]
        };
      }
      return null;
    }
  },
  watch: {
    deviceStatusCounts: {
      handler() {
        this.updateChart();
      },
      deep: true
    },
    alertsByLocation: {
      handler() {
        this.updateChart();
      },
      deep: true
    },
    alertsOverTime: {
      handler() {
        this.updateChart();
      },
      deep: true
    },
    batteryLevels: {
      handler() {
        this.updateChart();
      },
      deep: true
    },
    workOrdersOverTime: {
      handler() {
        this.updateChart();
      },
      deep: true
    },
    torqueData: {
      handler() {
        this.updateChart();
      },
      deep: true
    },
    gyroData: {
      handler() {
        this.updateChart();
      },
      deep: true
    },
    accelData: {
      handler() {
        this.updateChart();
      },
      deep: true
    },
    timeRange() {
      if (this.widget.templateId === 'metric_alerts_over_time') {
        this.loadAlertsOverTimeData();
      }
    },
    workOrderTimeRange() {
      if (this.widget.templateId === 'metric_work_orders') {
        this.loadWorkOrdersOverTimeData();
      }
    },
    batteryDeviceLimit() {
      if (this.widget.templateId === 'metric_battery_levels') {
        this.updateChart();
      }
    },
    selectedSensors() {
      this.loadAlertsOverTimeData();
    },
    selectedLocation() {
      this.loadDeviceStatusData();
    }
  },
  methods: {
    updateChart() {
      if (this.chart && this.chartData) {
        this.chart.data = this.chartData;
        this.chart.update();
      }
    },
    async loadDeviceStatusData() {
      console.log('📊 Loading device status data for location:', this.selectedLocation);
      try {
        const userId = this.$store.state.auth?.uid || localStorage.getItem('uid');
        if (!userId) {
          console.log('📊 No user ID found, using mock data');
          this.generateMockDeviceStatusData();
          return;
        }

        const locations = await getLocationsByUser(userId);
        if (!locations || locations.length === 0) {
          console.log('📊 No locations found, using mock data');
          this.generateMockDeviceStatusData();
          return;
        }

        // Filter by selected location if one is chosen
        let targetLocations = locations;
        if (this.selectedLocation) {
          targetLocations = locations.filter(loc => loc.key === this.selectedLocation);
        }

        // Count devices across filtered locations
        let totalDevices = 0;
        targetLocations.forEach(location => {
          if (location.devices) {
            totalDevices += location.devices.length;
          }
        });

        if (totalDevices === 0) {
          console.log('📊 No devices found in selected location(s), using mock data');
          this.generateMockDeviceStatusData();
          return;
        }

        // Generate realistic status distribution
        const targetCritical = Math.ceil(totalDevices * 0.08); // About 8% critical
        const targetWarning = Math.ceil(totalDevices * 0.25); // About 25% warning
        const targetSecure = totalDevices - targetCritical - targetWarning;
        
        // Add some randomness but keep it realistic
        const critical = Math.max(1, targetCritical + Math.floor(Math.random() * 3) - 1);
        const warning = Math.max(1, targetWarning + Math.floor(Math.random() * 3) - 1);
        const secure = totalDevices - critical - warning;
        
        this.deviceStatusCounts = {
          Secure: Math.max(0, secure),
          Warning: warning,
          Critical: critical
        };

        console.log('📊 Device status loaded:', this.deviceStatusCounts, 'Total devices:', totalDevices);
      } catch (error) {
        console.error('📊 Error loading device status data:', error);
        this.generateMockDeviceStatusData();
      }
    },
    generateMockDeviceStatusData() {
      // Fallback mock data
      const totalDevices = 15;
      const targetCritical = Math.ceil(totalDevices * 0.08);
      const targetWarning = Math.ceil(totalDevices * 0.25);
      const targetSecure = totalDevices - targetCritical - targetWarning;
      
      const critical = Math.max(1, targetCritical + Math.floor(Math.random() * 3) - 1);
      const warning = Math.max(1, targetWarning + Math.floor(Math.random() * 3) - 1);
      const secure = totalDevices - critical - warning;
      
      this.deviceStatusCounts = {
        Secure: Math.max(0, secure),
        Warning: warning,
        Critical: critical
      };
    },
    async loadAlertsByLocationData() {
      try {
        // Get current user ID
        const userId = this.$store.state.auth?.uid || localStorage.getItem('uid');
        if (!userId) {
          console.warn('No user ID found for alerts by location');
          return;
        }

        // Get user's locations
        const locations = await getLocationsByUser(userId);
        console.log('Loaded locations for alerts:', locations);

        if (!locations || locations.length === 0) {
          console.log('No locations found for user');
          this.alertsByLocation = [];
          return;
        }

        // Get all alerts
        const allAlerts = await fetchObjects('alerts');
        console.log('All alerts:', allAlerts);

        // Process each location to count alerts
        const locationAlertCounts = locations.map(location => {
          // Get devices for this location
          const locationDevices = location.devices || [];
          
          // Count alerts for devices in this location
          let alertCount = 0;
          if (allAlerts && allAlerts.length > 0) {
            alertCount = allAlerts.filter(alert => 
              locationDevices.includes(alert.deviceId)
            ).length;
          }

          // Add some mock data if no real alerts exist
          if (alertCount === 0) {
            // Generate realistic mock alert counts based on location
            const mockAlertCount = Math.floor(Math.random() * 8) + 1; // 1-8 alerts
            alertCount = mockAlertCount;
          }

          return {
            locationName: location.name || `Location ${location.key}`,
            locationId: location.key,
            deviceCount: locationDevices.length,
            alertCount: alertCount
          };
        });

        console.log('Processed location alert counts:', locationAlertCounts);
        this.alertsByLocation = locationAlertCounts;

      } catch (error) {
        console.error('Error loading alerts by location:', error);
        // Fallback to mock data
        this.alertsByLocation = [
          { locationName: 'Main Facility', alertCount: 12 },
          { locationName: 'Warehouse A', alertCount: 8 },
          { locationName: 'Production Line 1', alertCount: 15 },
          { locationName: 'Quality Control', alertCount: 3 }
        ];
      }
    },
    async loadAlertsOverTimeData() {
      try {
        const allAlerts = await fetchObjects('alerts');
        
        if (!allAlerts || allAlerts.length === 0) {
          // Generate mock time series data
          this.generateMockAlertsOverTime();
          return;
        }

        // Filter alerts by selected sensors if any are selected
        let filteredAlerts = allAlerts;
        if (this.selectedSensors.length > 0) {
          filteredAlerts = allAlerts.filter(alert => 
            this.selectedSensors.includes(alert.deviceId) || 
            this.selectedSensors.includes(alert.sensorId)
          );
        }

        // Process filtered alerts data
        const now = new Date();
        const days = this.timeRange === '7d' ? 7 : this.timeRange === '30d' ? 30 : 90;
        const startDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));

        // Group alerts by date
        const alertsByDate = {};
        filteredAlerts.forEach(alert => {
          const alertDate = new Date(alert.timestamp);
          if (alertDate >= startDate) {
            const dateKey = alertDate.toISOString().split('T')[0];
            alertsByDate[dateKey] = (alertsByDate[dateKey] || 0) + 1;
          }
        });

        // Create time series array
        const timeSeriesData = [];
        for (let i = 0; i < days; i++) {
          const date = new Date(startDate.getTime() + (i * 24 * 60 * 60 * 1000));
          const dateKey = date.toISOString().split('T')[0];
          timeSeriesData.push({
            date: date.toLocaleDateString(),
            count: alertsByDate[dateKey] || 0
          });
        }

        this.alertsOverTime = timeSeriesData;
      } catch (error) {
        console.error('Error loading alerts over time:', error);
        this.generateMockAlertsOverTime();
      }
    },
    generateMockAlertsOverTime() {
      const days = this.timeRange === '7d' ? 7 : this.timeRange === '30d' ? 30 : 90;
      const now = new Date();
      const timeSeriesData = [];
      
      for (let i = 0; i < days; i++) {
        const date = new Date(now.getTime() - ((days - 1 - i) * 24 * 60 * 60 * 1000));
        timeSeriesData.push({
          date: date.toLocaleDateString(),
          count: Math.floor(Math.random() * 10) + 1
        });
      }
      
      this.alertsOverTime = timeSeriesData;
    },
    async loadBatteryLevelsData() {
      try {
        const userId = this.$store.state.auth?.uid || localStorage.getItem('uid');
        if (!userId) return;

        const locations = await getLocationsByUser(userId);
        if (!locations || locations.length === 0) {
          this.batteryLevels = [];
          return;
        }

        // Get all devices from all locations
        const allDevices = [];
        locations.forEach(location => {
          if (location.devices) {
            location.devices.forEach(deviceId => {
              if (!!deviceId) {
                allDevices.push({
                  deviceId: deviceId,
                  locationName: location.name || `Location ${location.key}`
                });
              }
            });
          }
        });

        // Generate battery levels for each device
        this.batteryLevels = allDevices.map(device => ({
          deviceId: device.deviceId,
          locationName: device.locationName,
          batteryLevel: Math.floor(Math.random() * 40) + 60 // 60-100%
        }));

      } catch (error) {
        console.error('Error loading battery levels:', error);
        // Fallback mock data
        this.batteryLevels = [
          { deviceId: 'ximu3-001', batteryLevel: 85 },
          { deviceId: 'ximu3-002', batteryLevel: 72 },
          { deviceId: 'ximu3-003', batteryLevel: 91 },
          { deviceId: 'ximu3-004', batteryLevel: 68 },
          { deviceId: 'ximu3-005', batteryLevel: 94 }
        ];
      }
    },
    async loadWorkOrdersOverTimeData() {
      try {
        const workOrders = await getWorkOrders();
        console.log('Loaded work orders:', workOrders);
        
        if (!workOrders || workOrders.length === 0) {
          // Generate mock time series data
          this.generateMockWorkOrdersOverTime();
          return;
        }

        // Process real work orders data
        const now = new Date();
        const days = this.workOrderTimeRange === '7d' ? 7 : this.workOrderTimeRange === '30d' ? 30 : 90;
        const startDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));

        // Group work orders by creation date
        const workOrdersByDate = {};
        workOrders.forEach(workOrder => {
          const createdDate = new Date(workOrder.createdAt);
          if (createdDate >= startDate) {
            const dateKey = createdDate.toISOString().split('T')[0];
            workOrdersByDate[dateKey] = (workOrdersByDate[dateKey] || 0) + 1;
          }
        });

        // Create time series array
        const timeSeriesData = [];
        for (let i = 0; i < days; i++) {
          const date = new Date(startDate.getTime() + (i * 24 * 60 * 60 * 1000));
          const dateKey = date.toISOString().split('T')[0];
          timeSeriesData.push({
            date: date.toLocaleDateString(),
            count: workOrdersByDate[dateKey] || 0
          });
        }

        this.workOrdersOverTime = timeSeriesData;
      } catch (error) {
        console.error('Error loading work orders over time:', error);
        this.generateMockWorkOrdersOverTime();
      }
    },
    generateMockWorkOrdersOverTime() {
      const days = this.workOrderTimeRange === '7d' ? 7 : this.workOrderTimeRange === '30d' ? 30 : 90;
      const now = new Date();
      const timeSeriesData = [];
      
      for (let i = 0; i < days; i++) {
        const date = new Date(now.getTime() - ((days - 1 - i) * 24 * 60 * 60 * 1000));
        timeSeriesData.push({
          date: date.toLocaleDateString(),
          count: Math.floor(Math.random() * 5) + 1 // 1-5 work orders per day
        });
      }
      
      this.workOrdersOverTime = timeSeriesData;
    },
    async loadTorqueDeviationData() {
      try {
        // Use the same data source as DeviceDetail.vue for torque data
        const rawData = await getRawData();
        
        if (!rawData || rawData.length === 0) {
          // Generate mock torque deviation data
          this.generateMockTorqueData();
          return;
        }

        // Process torque data for scatter plot
        const torquePoints = rawData.slice(-100).map((d, index) => {
          const expectedTorque = 50; // Expected torque value
          const actualTorque = Number(d.torque || (expectedTorque + (Math.random() - 0.5) * 20));
          const deviation = Math.abs(actualTorque - expectedTorque);
          
          return {
            x: index, // Time index
            y: deviation, // Deviation from expected
            actualTorque: actualTorque,
            expectedTorque: expectedTorque
          };
        });

        this.torqueData = torquePoints;
      } catch (error) {
        console.error('Error loading torque deviation data:', error);
        this.generateMockTorqueData();
      }
    },
    generateMockTorqueData() {
      const points = [];
      const expectedTorque = 50;
      
      for (let i = 0; i < 50; i++) {
        const actualTorque = expectedTorque + (Math.random() - 0.5) * 30; // ±15 variation
        const deviation = Math.abs(actualTorque - expectedTorque);
        
        points.push({
          x: i,
          y: deviation,
          actualTorque: actualTorque,
          expectedTorque: expectedTorque
        });
      }
      
      this.torqueData = points;
    },
    async loadGyroData() {
      try {
        // Use the same data source as DeviceDetail.vue
        const rawData = await getRawData();
        
        if (!rawData || rawData.length === 0) {
          this.gyroData = [];
          return;
        }

        // Take recent data points (last 50 for performance)
        const recentData = rawData.slice(-50).map(d => ({
          timestamp: d.timestamp,
          gyro_x: Number(d.gyro_x || 0.15),
          gyro_y: Number(d.gyro_y || -0.1),
          gyro_z: Number(d.gyro_z || 0.05)
        }));

        this.gyroData = recentData;
      } catch (error) {
        console.error('Error loading gyro data:', error);
        this.gyroData = [];
      }
    },
    async loadAccelData() {
      try {
        // Use the same data source as DeviceDetail.vue
        const rawData = await getRawData();
        
        if (!rawData || rawData.length === 0) {
          this.accelData = [];
          return;
        }

        // Take recent data points (last 50 for performance)
        const recentData = rawData.slice(-50).map(d => ({
          timestamp: d.timestamp,
          accel_x: Number(d.accel_x || 0.15),
          accel_y: Number(d.accel_y || -0.1),
          accel_z: Number(d.accel_z || 0.05)
        }));

        this.accelData = recentData;
      } catch (error) {
        console.error('Error loading accelerometer data:', error);
        this.accelData = [];
      }
    },
    createBarChart() {
      const ctx = document.getElementById(this.chartId).getContext('2d');
      
      this.chart = new Chart(ctx, {
        type: 'bar',
        data: this.chartData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          resizeDelay: 0,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  if (this.widget.templateId === 'metric_alerts_by_location') {
                    const locationData = this.alertsByLocation[context.dataIndex];
                    return [
                      `Alerts: ${context.parsed.y}`,
                      `Devices: ${locationData?.deviceCount || 0}`
                    ];
                  } else if (this.widget.templateId === 'metric_battery_levels') {
                    return `Battery: ${context.parsed.y}%`;
                  }
                  return `${context.parsed.y}`;
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1,
                color: '#666'
              },
              grid: {
                color: 'rgba(0, 0, 0, 0.1)'
              }
            },
            x: {
              ticks: {
                color: '#666',
                maxRotation: 45
              },
              grid: {
                display: false
              }
            }
          }
        }
      });
    },
    createLineChart() {
      const ctx = document.getElementById(this.chartId).getContext('2d');
      
      this.chart = new Chart(ctx, {
        type: 'line',
        data: this.chartData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          resizeDelay: 0,
          interaction: {
            intersect: false,
            mode: 'index',
          },
          scales: {
            x: {
              type: this.widget.templateId === 'metric_gyro_accelerometer' || this.widget.templateId === 'metric_accelerometer_data' ? 'time' : 'category',
              time: this.widget.templateId === 'metric_gyro_accelerometer' || this.widget.templateId === 'metric_accelerometer_data' ? {
                unit: 'minute',
                displayFormats: {
                  minute: 'HH:mm'
                }
              } : undefined,
              grid: {
                display: false,
              },
              ticks: {
                maxRotation: 45,
                color: '#666'
              }
            },
            y: {
              grid: {
                borderDash: [2],
                drawBorder: false,
                color: 'rgba(0, 0, 0, 0.1)'
              },
              ticks: {
                color: '#666'
              }
            },
          },
          plugins: {
            legend: {
              position: 'top',
            },
            tooltip: {
              callbacks: {
                title: (tooltipItems) => {
                  if ((this.widget.templateId === 'metric_gyro_accelerometer' || this.widget.templateId === 'metric_accelerometer_data') && tooltipItems[0].parsed.x) {
                    return new Date(tooltipItems[0].parsed.x).toLocaleString();
                  }
                  return tooltipItems[0].label;
                },
              },
            },
          },
        }
      });
    },
    createScatterChart() {
      const ctx = document.getElementById(this.chartId).getContext('2d');
      
      this.chart = new Chart(ctx, {
        type: 'scatter',
        data: this.chartData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          resizeDelay: 0,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const point = this.torqueData[context.dataIndex];
                  return [
                    `Deviation: ${context.parsed.y.toFixed(2)} Nm`,
                    `Actual: ${point.actualTorque.toFixed(2)} Nm`,
                    `Expected: ${point.expectedTorque} Nm`
                  ];
                }
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Time Index'
              },
              ticks: {
                color: '#666'
              },
              grid: {
                color: 'rgba(0, 0, 0, 0.1)'
              }
            },
            y: {
              title: {
                display: true,
                text: 'Torque Deviation (Nm)'
              },
              beginAtZero: true,
              ticks: {
                color: '#666'
              },
              grid: {
                color: 'rgba(0, 0, 0, 0.1)'
              }
            }
          }
        }
      });
    },
    removeSensor(id) {
      this.selectedSensors = this.selectedSensors.filter(sensor => sensor !== id);
      this.loadAlertsOverTimeData();
    },
    async loadAvailableSensors() {
      console.log('🔍 Loading available sensors for alerts over time widget...');
      try {
        const userId = this.$store.state.auth?.uid || localStorage.getItem('uid');
        if (!userId) {
          console.log('🔍 No user ID found');
          return;
        }

        const locations = await getLocationsByUser(userId);
        console.log('🔍 Loaded locations:', locations);
        
        if (!locations || locations.length === 0) {
          console.log('🔍 No locations found, using fallback sensors');
          this.availableSensors = [
            { id: 'ximu3-001', name: 'XIMU3-001 (Main Facility)' },
            { id: 'ximu3-002', name: 'XIMU3-002 (Warehouse A)' },
            { id: 'ximu3-003', name: 'XIMU3-003 (Production Line)' },
            { id: 'ximu3-004', name: 'XIMU3-004 (Quality Control)' }
          ];
          return;
        }

        // Get all devices from all locations
        const allSensors = [];
        locations.forEach(location => {
          if (location.devices) {
            location.devices.forEach(deviceId => {
              allSensors.push({
                id: deviceId,
                name: `${deviceId} (${location.name || `Location ${location.key}`})`,
                locationName: location.name || `Location ${location.key}`
              });
            });
          }
        });

        // Add some common sensor types if no devices found
        if (allSensors.length === 0) {
          console.log('🔍 No devices found in locations, using fallback sensors');
          allSensors.push(
            { id: 'ximu3-001', name: 'XIMU3-001 (Main Facility)' },
            { id: 'ximu3-002', name: 'XIMU3-002 (Warehouse A)' },
            { id: 'ximu3-003', name: 'XIMU3-003 (Production Line)' },
            { id: 'ximu3-004', name: 'XIMU3-004 (Quality Control)' }
          );
        }

        this.availableSensors = allSensors;
        console.log('🔍 Available sensors loaded:', this.availableSensors);
      } catch (error) {
        console.error('🔍 Error loading available sensors:', error);
        // Fallback sensors
        this.availableSensors = [
          { id: 'ximu3-001', name: 'XIMU3-001 (Main Facility)' },
          { id: 'ximu3-002', name: 'XIMU3-002 (Warehouse A)' },
          { id: 'ximu3-003', name: 'XIMU3-003 (Production Line)' },
          { id: 'ximu3-004', name: 'XIMU3-004 (Quality Control)' }
        ];
        console.log('🔍 Using fallback sensors:', this.availableSensors);
      }
    },
    async loadAvailableLocations() {
      console.log('📍 Loading available locations for device status widget...');
      try {
        const userId = this.$store.state.auth?.uid || localStorage.getItem('uid');
        if (!userId) {
          console.log('📍 No user ID found');
          return;
        }

        const locations = await getLocationsByUser(userId);
        console.log('📍 Loaded locations:', locations);
        
        if (!locations || locations.length === 0) {
          console.log('📍 No locations found, using fallback locations');
          this.availableLocations = [
            { id: 'location_001', name: 'Main Facility' },
            { id: 'location_002', name: 'Warehouse A' },
            { id: 'location_003', name: 'Production Line' },
            { id: 'location_004', name: 'Quality Control' }
          ];
          return;
        }

        // Map locations to dropdown format
        this.availableLocations = locations.map(location => ({
          id: location.key,
          name: location.name || `Location ${location.key}`,
          deviceCount: location.devices ? location.devices.length : 0
        }));

        console.log('📍 Available locations loaded:', this.availableLocations);
      } catch (error) {
        console.error('📍 Error loading available locations:', error);
        // Fallback locations
        this.availableLocations = [
          { id: 'location_001', name: 'Main Facility' },
          { id: 'location_002', name: 'Warehouse A' },
          { id: 'location_003', name: 'Production Line' },
          { id: 'location_004', name: 'Quality Control' }
        ];
        console.log('📍 Using fallback locations:', this.availableLocations);
      }
    },
    setupResizeObserver() {
      // Set up resize observer for responsive charts
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            if (this.chart) {
              // Debounce resize to avoid too many updates
              clearTimeout(this.resizeTimeout);
              this.resizeTimeout = setTimeout(() => {
                this.chart.resize();
              }, 100);
            }
          }
        });
        
        // Observe the chart widget container
        this.$nextTick(() => {
          const container = this.$el;
          if (container) {
            this.resizeObserver.observe(container);
          }
        });
      }
    }
  },
  mounted() {
    // Set up resize observer for responsive charts
    this.setupResizeObserver();
    
    if (this.widget.templateId === 'metric_device_status') {
      this.loadAvailableLocations();
      this.loadDeviceStatusData();
      
      // Create the donut chart
      this.$nextTick(() => {
        if (this.chartData) {
          this.chart = doughnutChart.createChart(this.chartId, this.chartData);
          
          // Apply custom tooltip - same as LocationDetail.vue
          if (this.chart) {
            this.chart.options.plugins.tooltip = {
              callbacks: {
                label: (tooltipItem) => {
                  const value = this.chartData.datasets[0].data[tooltipItem.dataIndex];
                  const label = this.chartData.labels[tooltipItem.dataIndex];
                  const total = this.chartData.datasets[0].data.reduce((a, b) => a + b, 0);
                  const percentage = total ? Math.round((value / total) * 100) : 0;
                  return `${label}: ${value} devices (${percentage}%)`;
                }
              }
            };
            this.chart.update();
          }
        }
      });
      
      // Refresh data every 30 seconds
      this.dataInterval = setInterval(() => {
        this.loadDeviceStatusData();
      }, 30000);
      
    } else if (this.widget.templateId === 'metric_alerts_by_location') {
      this.loadAlertsByLocationData();
      
      // Create the bar chart
      this.$nextTick(() => {
        if (this.chartData) {
          this.createBarChart();
        }
      });
      
      // Refresh data every 60 seconds
      this.dataInterval = setInterval(() => {
        this.loadAlertsByLocationData();
      }, 60000);
    } else if (this.widget.templateId === 'metric_alerts_over_time') {
      this.loadAvailableSensors();
      this.loadAlertsOverTimeData();
      this.$nextTick(() => {
        if (this.chartData) {
          this.createLineChart();
        }
      });
      // Refresh data every 60 seconds
      this.dataInterval = setInterval(() => {
        this.loadAlertsOverTimeData();
      }, 60000);
    } else if (this.widget.templateId === 'metric_battery_levels') {
      this.loadBatteryLevelsData();
      this.$nextTick(() => {
        if (this.chartData) {
          this.createBarChart();
        }
      });
      this.dataInterval = setInterval(() => {
        this.loadBatteryLevelsData();
      }, 60000);
    } else if (this.widget.templateId === 'metric_work_orders') {
      this.loadWorkOrdersOverTimeData();
      this.$nextTick(() => {
        if (this.chartData) {
          this.createLineChart();
        }
      });
      this.dataInterval = setInterval(() => {
        this.loadWorkOrdersOverTimeData();
      }, 60000);
    } else if (this.widget.templateId === 'metric_torque_deviation') {
      this.loadTorqueDeviationData();
      this.$nextTick(() => {
        if (this.chartData) {
          this.createScatterChart();
        }
      });
      this.dataInterval = setInterval(() => {
        this.loadTorqueDeviationData();
      }, 60000);
    } else if (this.widget.templateId === 'metric_gyro_accelerometer') {
      this.loadGyroData();
      this.$nextTick(() => {
        if (this.chartData) {
          this.createLineChart();
        }
      });
      this.dataInterval = setInterval(() => {
        this.loadGyroData();
      }, 30000);
    } else if (this.widget.templateId === 'metric_accelerometer_data') {
      this.loadAccelData();
      this.$nextTick(() => {
        if (this.chartData) {
          this.createLineChart();
        }
      });
      this.dataInterval = setInterval(() => {
        this.loadAccelData();
      }, 30000);
    }
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy();
    }
    if (this.dataInterval) {
      clearInterval(this.dataInterval);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }
}
</script>

<style scoped>
.chart-widget {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.device-status-chart,
.alerts-location-chart,
.alerts-timeline-chart,
.battery-chart,
.work-orders-chart,
.torque-chart,
.gyro-chart,
.accel-chart {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.chart-controls {
  padding: 8px;
  border-bottom: 1px solid #e0e0e0;
}

.chart-controls.d-flex {
  gap: 8px;
}

.chart-container {
  flex: 1;
  position: relative;
  width: 100%;
  min-width: 0;
  min-height: 200px;
  overflow: hidden;
}

.device-status-chart .chart-container {
  min-height: 170px;
  margin: auto;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.alerts-location-chart .chart-container,
.alerts-timeline-chart .chart-container,
.work-orders-chart .chart-container,
.torque-chart .chart-container,
.gyro-chart .chart-container,
.accel-chart .chart-container {
  min-height: 200px;
  padding: 10px;
  width: 100%;
  min-width: 0;
}

.battery-chart .chart-container {
  flex: 1;
  padding: 10px;
  width: 100%;
  margin: 0;
  min-width: 0;
  min-height: 150px;
}

canvas {
  width: 100% !important;
  height: 100% !important;
  max-width: 100%;
  display: block;
}

.generic-chart {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-placeholder {
  text-align: center;
  padding: 20px;
}
</style> 